import { BASE_API, CREDENCIAIS } from './base.js';
import { erroDaResposta } from './erros.js';
import type { paths } from './schema.js';

/**
 * Cliente HTTP tipado do middleware.
 *
 * Os tipos vêm de `schema.ts`, gerado do `openapi.json` do `wms-api`
 * (`npm run generate:api`). Se um endpoint mudar e o schema não for
 * regenerado, o compilador acusa — o contrato não silencia.
 *
 * Toda falha sai daqui como `ErroApi`, com o CÓDIGO do contrato: é por ele
 * que a tela decide, nunca pela mensagem (ver `erros.ts`).
 */

/**
 * Tempo limite de toda requisição do painel.
 *
 * Sem ele, um middleware que aceita a conexão e nunca responde deixa a tela
 * em "Carregando…" para sempre: sem dado, sem erro e sem caminho de volta.
 * Vinte segundos é folgado para a consulta mais cara do painel (RNF-006 pede
 * 3s com 5.000 movimentações) e curto o bastante para virar um erro que a
 * pessoa pode tentar de novo.
 */
export const TEMPO_LIMITE_MS = 20_000;

/**
 * Sinal de cancelamento da requisição.
 *
 * O sinal de quem chama vence — quem cancela por conta própria já decidiu o
 * ciclo de vida da chamada. Sem sinal, entra o tempo limite padrão.
 */
function sinalDaRequisicao(sinal?: AbortSignal): AbortSignal | undefined {
  if (sinal !== undefined) {
    return sinal;
  }

  return typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(TEMPO_LIMITE_MS)
    : undefined;
}

/** Caminho declarado no OpenAPI. Endpoint removido vira erro de compilação. */
export type CaminhoDoContrato = Extract<keyof paths, string>;

/** Só os caminhos do OpenAPI que expõem GET. */
type CaminhosGet = {
  [C in keyof paths]: paths[C] extends { get: unknown } ? C : never;
}[keyof paths];

type RespostaGet<C extends CaminhosGet> = paths[C] extends {
  get: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : never;

/**
 * Opções da requisição.
 *
 * `headers` é estreitado para um registro simples de propósito: o
 * `HeadersInit` do DOM também aceita `Headers` e array de pares, formas que
 * não sobrevivem ao spread — o cabeçalho `Accept` sumiria em silêncio.
 */
export type OpcoesRequisicao = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

/**
 * GET tipado pelo caminho do OpenAPI.
 *
 * @throws {ErroApi} quando a resposta não é 2xx.
 */
export async function get<C extends CaminhosGet>(
  caminho: C,
  init?: OpcoesRequisicao,
): Promise<RespostaGet<C>> {
  const sinal = sinalDaRequisicao(init?.signal ?? undefined);

  const resposta = await fetch(`${BASE_API}${caminho}`, {
    credentials: CREDENCIAIS,
    ...init,
    ...(sinal ? { signal: sinal } : {}),
    headers: { Accept: 'application/json', ...init?.headers },
  });

  if (!resposta.ok) {
    throw await erroDaResposta(resposta, caminho);
  }

  return (await resposta.json()) as RespostaGet<C>;
}

/**
 * Monta o caminho concreto a partir do modelo do OpenAPI.
 *
 * O modelo é conferido contra o contrato gerado, e cada valor é escapado: um
 * código de produto com barra (`SKU/01`) não pode virar outra rota.
 *
 * @throws {Error} se sobrar parâmetro sem valor — chamar `/dispositivos/{id}`
 * literalmente seria um 404 misterioso em produção.
 */
export function montarCaminho(
  modelo: CaminhoDoContrato,
  valores: Readonly<Record<string, string>> = {},
): string {
  const caminho = modelo.replace(/\{(\w+)\}/g, (_, nome: string) => {
    const valor = valores[nome];

    if (valor === undefined) {
      throw new Error(`O caminho "${modelo}" exige o parâmetro "${nome}".`);
    }

    return encodeURIComponent(valor);
  });

  return caminho;
}

/** Valor aceito em query string. `undefined`, `null` e texto vazio somem. */
export type ValorDeConsulta = string | number | boolean | undefined | null;

export type OpcoesDeRequisicao = {
  readonly metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly corpo?: unknown;
  readonly consulta?: Readonly<Record<string, ValorDeConsulta>>;
  readonly sinal?: AbortSignal;
};

/**
 * Serializa o filtro da tela.
 *
 * Filtro não informado não vira `?produto=undefined`; `false` continua indo,
 * porque `origemIndeterminada=false` é um recorte de verdade (UC08).
 */
function consultaSerializada(consulta: Readonly<Record<string, ValorDeConsulta>>): string {
  const parametros = new URLSearchParams();

  for (const [nome, valor] of Object.entries(consulta)) {
    if (valor === undefined || valor === null || valor === '') {
      continue;
    }

    parametros.set(nome, String(valor));
  }

  const texto = parametros.toString();
  return texto.length > 0 ? `?${texto}` : '';
}

/**
 * Requisição a um caminho já montado (`montarCaminho`).
 *
 * Devolve o corpo **sem validar**: quem chama conhece o formato e o confere
 * com zod (`validacao.ts`). 204 devolve `null` — bloquear/desbloquear não
 * precisam responder nada.
 *
 * @throws {ErroApi} quando a resposta não é 2xx.
 */
export async function requisitar(
  caminho: string,
  opcoes: OpcoesDeRequisicao = {},
): Promise<unknown> {
  const temCorpo = opcoes.corpo !== undefined;
  const sinal = sinalDaRequisicao(opcoes.sinal);
  const url = `${BASE_API}${caminho}${opcoes.consulta ? consultaSerializada(opcoes.consulta) : ''}`;

  const resposta = await fetch(url, {
    method: opcoes.metodo ?? 'GET',
    credentials: CREDENCIAIS,
    headers: temCorpo
      ? { Accept: 'application/json', 'Content-Type': 'application/json' }
      : { Accept: 'application/json' },
    ...(temCorpo ? { body: JSON.stringify(opcoes.corpo) } : {}),
    ...(sinal ? { signal: sinal } : {}),
  });

  if (!resposta.ok) {
    throw await erroDaResposta(resposta, caminho);
  }

  if (resposta.status === 204) {
    return null;
  }

  return (await resposta.json()) as unknown;
}
